import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import ConversationsList from './pages/ConversationsList';
import ConversationView from './pages/ConversationView';
import Contacts from './pages/Contacts';
import Templates from './pages/Templates';
import OptInManagement from './pages/OptInManagement';
import EmbeddedSignup from './pages/EmbeddedSignup';
import BusinessAccounts from './pages/BusinessAccounts';
import BulkMessaging from './pages/BulkMessaging';
import BaseDados from './pages/BaseDados';
import Settings from './pages/Settings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Login from './pages/Login';
import Users from './pages/Users';
import Sectors from './pages/Sectors';
import Leads from './pages/Leads';
import Tasks from './pages/Tasks';
import Automations from './pages/Automations';
import Reports from './pages/Reports';
import AIAgents from './pages/AIAgents';
import PhoneNumbers from './pages/PhoneNumbers';
import Profile from './pages/Profile';
import MyConversations from './pages/MyConversations';
import TenantsManagement from './pages/TenantsManagement';
import TenantRegister from './pages/TenantRegister';
import MasterDashboard from './pages/MasterDashboard';
import MasterTenants from './pages/MasterTenants';
import MasterReports from './pages/MasterReports';
import MasterAuditLogs from './pages/MasterAuditLogs';
import MasterUsers from './pages/MasterUsers';
import MasterSettings from './pages/MasterSettings';
import ForgotPassword from './pages/ForgotPassword';
import AttendeeDashboard from './pages/AttendeeDashboard';
import TeamPerformance from './pages/TeamPerformance';
import AchievementsPage from './pages/AchievementsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import BIIntegration from './pages/BIIntegration';
import Envios from './pages/Envios';
import BulkPdfSender from './pages/BulkPdfSender';
import Chatbots from './pages/Chatbots';
import ProtectedRoute from './components/ProtectedRoute';
import TemplateVariables from './pages/TemplateVariables';
import FunnelBoard from './pages/FunnelBoard';
import ProfessionalConfig from './pages/ProfessionalConfig';
import heartbeat from './services/heartbeat';
import { LanguageProvider } from './contexts/LanguageContext';
import Campaigns from './pages/Campaigns';

function RootRedirect() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.role === 'master_admin') {
        navigate('/master-admin/dashboard', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      console.error('Erro ao decodificar token:', err);
      localStorage.removeItem('token');
      navigate('/login');
    }
  }, [navigate]);

  return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
    </div>
  );
}

function PrivateRoute({ children }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  // ⭐ Iniciar heartbeat quando o app carrega
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      heartbeat.start();
    }

    // Parar heartbeat quando desmontar
    return () => {
      heartbeat.stop();
    };
  }, []);

  return (
    // ⭐ NOVO: LanguageProvider envolvendo toda a aplicação
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          {/* ============================================ */}
          {/* ⭐ ROTAS PÚBLICAS (SEM LOGIN) */}
          {/* ============================================ */}
          <Route path="/login" element={<Login />} />
          <Route path="/register-company" element={<TenantRegister />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Redirecionamento inteligente da raiz */}
          <Route path="/" element={<RootRedirect />} />

          {/* ⭐ NOVO: ponte usada pelo login (window.location.replace), já que a raiz "/"
              agora é a página de boas-vindas pública no Nginx. Carrega o app normalmente
              e manda pro Dashboard por dentro, sem precisar pedir nada de novo ao servidor. */}
          <Route path="/dashboard" element={<Navigate to="/" replace />} />

          {/* ============================================ */}
          {/* ⭐ ROTAS PROTEGIDAS COM PROTEÇÃO MÁXIMA ⭐ */}
          {/* ============================================ */}
          <Route path="/" element={
            <PrivateRoute>
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            </PrivateRoute>
          }>
            {/* Dashboard */}
            <Route index element={<Dashboard />} />
            
            {/* Rotas do Master Admin */}
            <Route path="master-admin/dashboard" element={<MasterDashboard />} />
            <Route path="master-admin/tenants" element={<MasterTenants />} />
            <Route path="master-admin/reports" element={<MasterReports />} />
            <Route path="master-admin/audit-logs" element={<MasterAuditLogs />} />
            <Route path="master-admin/users" element={<MasterUsers />} />
            <Route path="master-admin/settings" element={<MasterSettings />} />
            
            {/* Atendimento */}
            <Route path="conversations" element={<ConversationsList />} />
            <Route path="conversation/:id" element={<ConversationView />} />
            <Route path="my-conversations" element={<MyConversations />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="funnel-board" element={<FunnelBoard />} />
            
            {/* CRM */}
            <Route path="leads" element={<Leads />} />
            
            {/* Monitoramento */}
            <Route path="team-performance" element={<TeamPerformance />} />
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="bi-integration" element={<BIIntegration />} />
            <Route path="envios" element={<Envios />} />
            <Route path="achievements" element={<AchievementsPage />} />
            <Route path="attendee-dashboard" element={<AttendeeDashboard />} />
            
            {/* Administração */}
            <Route path="users" element={<Users />} />
            <Route path="sectors" element={<Sectors />} />
            <Route path="phone-numbers" element={<PhoneNumbers />} />
            
            {/* Comunicação */}
            <Route path="templates" element={<Templates />} />
            <Route path="bulk-messaging" element={<BulkMessaging />} />
            <Route path="base-dados" element={<BaseDados />} />
            <Route path="automations" element={<Automations />} />
            <Route path="ai-agents" element={<AIAgents />} />
            <Route path="opt-in" element={<OptInManagement />} />
            <Route path="chatbots" element={<Chatbots />} />
            
            {/* Relatórios e Business */}
            <Route path="reports" element={<Reports />} />
            <Route path="business-accounts" element={<BusinessAccounts />} />
           <Route path="/campaigns" element={<Campaigns />} />
 
            {/* Perfil */}
            <Route path="profile" element={<Profile />} />
            
            {/* Outras rotas */}
            <Route path="tenants" element={<TenantsManagement />} />
            <Route path="settings" element={<Settings />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="embedded-signup" element={<EmbeddedSignup />} />
            <Route path="bulk-pdfs" element={<BulkPdfSender />} />

            {/* Template Variables */}
            <Route path="template-variables" element={<TemplateVariables />} />
            <Route path="professional-config" element={<ProfessionalConfig />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
